import {Card, Chip, Badge, Text} from '@albert-io/atomic';
import {useDroppable, useDraggable} from '@dnd-kit/core';
import cx from 'classnames';
import {useShallow} from 'zustand/react/shallow';
import React, {useEffect, useMemo} from 'react';
import {supplementRegexBaseString} from 'client/generic/MarkdownRendererV2/rules/supplementRules';
import {makeParser} from 'generic/MarkdownRendererV2/markdownRendererUtils';
import classNames from 'classnames';
import type {StoreInstanceType} from '../../V2/useQuestionStores';

const isSupplement = (value: string) => {
  const regex = new RegExp(`^${supplementRegexBaseString}`);
  return regex.test(value);
};

const getSupplementRenderer = (value: string) => {
  const parser = makeParser();
  const syntaxTree = parser.makeSyntaxTree(value);
  return parser.reactOutput(syntaxTree);
};

const renderStringOrSupplement = (value: string) => {
  if (isSupplement(value)) {
    return getSupplementRenderer(value);
  }
  return value;
};

// import types from questionStore and use here instead of any once they are added
interface MovableItemProps {
  item: {id: string; value: string; placedInSlot: string | null};
  isActive: boolean;
  activeItemId?: string | null;
  onClick: (e: React.MouseEvent) => void;
  isDragging?: boolean;
  disabled?: boolean;
  isSubmitted?: boolean;
  isCorrect?: boolean;
}

export const MovableItem = ({
  item,
  activeItemId,
  isActive,
  onClick,
  isDragging,
  isSubmitted,
  isCorrect
}: MovableItemProps) => {
  const {attributes, listeners, setNodeRef, transform} = useDraggable({id: item.id});

  const style = transform
    ? {
        transform: `translate3d(${transform?.x}px, ${transform?.y}px, 0)`
      }
    : {};

  const valueRenderer = renderStringOrSupplement(item.value);

  const handleClick = (e: React.MouseEvent) => {
    // Only handle clicks if we're not dragging
    // dnd-kit will handle the drag automatically
    e.stopPropagation();
    onClick(e);
  };

  return (
    <>
      {!isSubmitted && (
        <Chip
          as='button'
          color='brand'
          className={cx('m-x0! cursor-pointer border-0 object-contain', {
            '-rotate-6': isActive,
            'opacity-50': isDragging || (!isDragging && activeItemId !== null && !isActive),
            'z-10': isActive || isDragging
          })}
          style={style}
          ref={setNodeRef}
          onClick={handleClick}
          type='button'
          {...attributes}
          {...listeners}
        >
          {valueRenderer}
        </Chip>
      )}
      {isSubmitted && (
        <Badge
          size='large'
          status={isCorrect ? 'positive' : 'negative'}
          icon={isCorrect ? ['far', 'check-circle'] : ['far', 'circle-xmark']}
          className={cx('m-x0! cursor-pointer object-contain', {
            '-rotate-6': isActive,
            'opacity-50': isDragging,
            'z-10': isActive || isDragging
          })}
          roundness='square'
        >
          {valueRenderer}
        </Badge>
      )}
    </>
  );
};

interface DroppableItemProps {
  slot: {id: string; label?: string};
  useStoreHook: StoreInstanceType;
  onClick: () => void;
  onItemClick: (itemId: string, e?: React.MouseEvent) => void;
}

export const DroppableItem = ({slot, useStoreHook, onClick, onItemClick}: DroppableItemProps) => {
  const {isOver, setNodeRef, node} = useDroppable({id: slot.id});

  const {activeItemId, isDragging, isSubmitted, correctAnswer, wordBankItems} = useStoreHook(
    useShallow((state) => ({
      dropSlots: state.dropSlots,
      activeItemId: state.activeItemId,
      isDragging: state.isDragging,
      isSubmitted: state.isSubmitted,
      correctAnswer: state.correctAnswer,
      wordBankItems: state.wordBank
    }))
  );

  const placedItem = wordBankItems.find((item) => item.placedInSlot === slot.id);
  const isActiveTarget = activeItemId !== null && !isDragging;
  const handleSlotClick = (e: React.MouseEvent) => {
    // Stop propagation to prevent conflicts with item clicks
    e.stopPropagation();
    onClick();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  const valueRenderer = slot.label ? renderStringOrSupplement(slot.label) : null;

  useEffect(() => {
    const clickHandler = (e: MouseEvent) => {
      // only handle clicks if an item is active
      if (activeItemId === null) return;
      if (node.current && node.current.contains(e.target as Node)) {
        e.stopPropagation();
        onClick();
      }
    };
    document.addEventListener('click', clickHandler);
    return () => {
      document.removeEventListener('click', clickHandler);
    };
  }, [onClick, node, activeItemId]);

  const isCorrect = useMemo(() => {
    const bankId = placedItem?.id;
    const slotId = slot.id;
    const slotAnswerId = correctAnswer ? correctAnswer[slotId] : null;
    return isSubmitted && slotAnswerId !== null && bankId === slotAnswerId;
  }, [correctAnswer, slot, placedItem, isSubmitted]);

  const correctAnswerForSlot = useMemo(() => {
    const correctAnswerId = correctAnswer ? correctAnswer[slot.id] : null;
    const correctAnswerItem = wordBankItems.find((item) => item.id === correctAnswerId);
    return correctAnswerItem ? renderStringOrSupplement(correctAnswerItem.value) : null;
  }, [correctAnswer, slot, wordBankItems]);

  return (
    <Card
      ref={setNodeRef}
      paddingLevel={1}
      className={classNames('mcq__options-grid flex flex-col items-center object-contain', {
        'border-2! border-selected! background-subtle': isOver || isActiveTarget,
        'border-2! border-positive!': isSubmitted && isCorrect,
        'border-2! border-negative!': isSubmitted && !isCorrect
      })}
    >
      {valueRenderer}
      <div className='min-h-10 mb-x2 flex items-center self-stretch'>
        <div
          onClick={handleSlotClick}
          onKeyDown={handleKeyDown}
          role='button'
          tabIndex={0}
          aria-label={placedItem ? `Slot with ${placedItem.value}` : 'Empty slot'}
          className={cx(
            'flex min-h-x8 w-full cursor-pointer items-center justify-center rounded-lg text-center',
            {
              'border-dotted border-content-brand background-brand-subtle-hover':
                !isSubmitted && (isOver || isActiveTarget),
              'border-solid border-primary': !isSubmitted && !isActiveTarget,
              'border-0!': isSubmitted
            }
          )}
        >
          {placedItem && (
            <div
              className='flex flex-col justify-center text-center'
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              role='presentation'
            >
              <MovableItem
                key={placedItem.id}
                item={placedItem}
                isActive={activeItemId === placedItem.id}
                activeItemId={activeItemId}
                isDragging={isDragging}
                onClick={(e) => {
                  if (!isSubmitted) {
                    onItemClick(placedItem.id, e);
                  }
                }}
                isCorrect={isCorrect}
                isSubmitted={isSubmitted}
              />
              {isSubmitted && !isCorrect && (
                <Badge className='mt-x1 justify-center' size='large' roundness='square'>
                  {correctAnswerForSlot}
                </Badge>
              )}
              {isSubmitted && isCorrect && <div className='mt-x1 min-h-x4' />}
            </div>
          )}
          {!placedItem && isSubmitted && !isCorrect && (
            <div className='flex flex-col justify-center' role='presentation'>
              <Badge
                size='large'
                status='negative'
                icon={['far', 'circle-xmark']}
                roundness='square'
              >
                No guess
              </Badge>
              <Badge className='mt-x1 justify-center' size='large' roundness='square'>
                {correctAnswerForSlot}
              </Badge>
            </div>
          )}
          {(isOver || isActiveTarget) && !placedItem && <Text color='brand'>Place here</Text>}
        </div>
      </div>
    </Card>
  );
};
